import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageShell from "@/components/PageShell";
import { MACHINES } from "@/lib/pages-data";

const SITE = "https://thecoventures.com";

export default function ProductIndexPage() {
  const list = Object.values(MACHINES);

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: list.map((m, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: m.name,
      url: `${SITE}/products/${m.slug}`,
    })),
  };

  return (
    <PageShell>
      <Seo
        title="Vending Machines for Sale in India — Snacks, PPE & Coffee | Thecoventures"
        description="Browse the Refuel range of vending machines — snacks, beverage, PPE and high-volume machines for Indian offices, factories and hospitals. UPI cashless, pan-India AMC."
        canonical={`${SITE}/products`}
        keywords="vending machine India, snacks vending machine, PPE vending machine, cashless vending machine, refuel vending"
        jsonLd={itemListSchema}
      />
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-8 md:py-14">
        <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 mb-3">
          The Refuel range
        </div>
        <h1
          data-testid="products-h1"
          className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tighter text-zinc-950 max-w-3xl"
        >
          Vending machines for Indian workplaces.
        </h1>
        <p className="mt-5 text-zinc-600 max-w-3xl md:text-lg">
          Refuel is a family of smart, cashless vending machines engineered for
          Indian offices, factories, hospitals, campuses and retail floors. Every
          machine ships UPI-ready with QR, card and wallet payments and streams
          real-time stock &amp; sales telemetry to a cloud dashboard so your
          pantry team never runs blind.
        </p>
        <p className="mt-4 text-zinc-600 max-w-3xl">
          Pick a model based on your footfall &mdash; the compact{" "}
          <strong>Refuel LITE</strong> for teams of 25&ndash;100, the flagship{" "}
          <strong>Refuel CORE</strong> for offices that want up to 300 SKUs with
          chilled beverages, the high-volume <strong>Refuel MAX</strong> with a
          21-inch touchscreen for 500+ daily footfall sites, and the{" "}
          <strong>Refuel SAFE</strong> PPE / consumables dispenser for plants,
          hospitals and labs where auditable issuance matters. All machines are
          backed by a pan-India AMC network with a 98% uptime SLA. Prices start
          from &#8377;1.5&nbsp;lakhs (transport extra) &mdash; write to us and
          we&apos;ll send a written quote within 24 hours.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="rounded-3xl border border-zinc-200 bg-zinc-50 p-5 md:p-7">
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-700 mb-4">
            Compare the range at a glance
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div className="bg-white rounded-2xl border border-zinc-200 p-4">
              <div className="font-display text-base font-semibold text-zinc-950">Refuel LITE</div>
              <div className="text-xs text-zinc-500 mt-1">Compact cashless</div>
              <div className="text-xs text-zinc-700 mt-3">Up to 180 items · UPI / Card / Wallet · Best for 25&ndash;100-employee offices.</div>
            </div>
            <div className="bg-white rounded-2xl border border-zinc-200 p-4">
              <div className="font-display text-base font-semibold text-zinc-950">Refuel CORE</div>
              <div className="text-xs text-zinc-500 mt-1">Snack &amp; beverage flagship</div>
              <div className="text-xs text-zinc-700 mt-3">Up to 300 items · chilled zone · HD touchscreen · IoT telemetry.</div>
            </div>
            <div className="bg-white rounded-2xl border border-zinc-200 p-4">
              <div className="font-display text-base font-semibold text-zinc-950">Refuel MAX</div>
              <div className="text-xs text-zinc-500 mt-1">High-volume</div>
              <div className="text-xs text-zinc-700 mt-3">400+ items · 21-inch touchscreen · engineered for 500+ daily footfall.</div>
            </div>
            <div className="bg-white rounded-2xl border border-zinc-200 p-4">
              <div className="font-display text-base font-semibold text-zinc-950">Refuel SAFE</div>
              <div className="text-xs text-zinc-500 mt-1">PPE dispenser</div>
              <div className="text-xs text-zinc-700 mt-3">Gloves, masks, glasses &amp; consumables with RFID / employee-ID auth and audit logs.</div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {list.map((m) => (
            <Link
              key={m.slug}
              to={`/products/${m.slug}`}
              data-testid={`product-card-${m.slug}`}
              className="group rounded-3xl border border-zinc-200 bg-white p-6 md:p-7 hover:border-emerald-400 hover:-translate-y-1 hover:shadow-xl hover:shadow-zinc-900/5 transition"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1 inline-block mb-4">
                {m.tag}
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-zinc-950">
                {m.name}
              </h2>
              <p className="mt-2 text-zinc-600">{m.subh}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-zinc-900 group-hover:text-emerald-700 transition">
                View details <ArrowUpRight className="w-4 h-4" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </PageShell>
  );
}
